// Products, prices, templates and sizes. Prices here are the defaults; /admin → Pricing overrides them.
// Names and descriptions shown to owners live in i18n.ts under product.<id>.*

export type Kind = 'image' | 'video';
export type ProductId = 'image_one' | 'image_pro' | 'image_pack5' | 'video_photo' | 'video_upgrade' | 'video_hai' | 'video_ai';

export interface Product {
  id: ProductId;
  kind: Kind;
  price: number;
  popular?: boolean;
  upgrade?: boolean;
  allFormats?: boolean;
  credit?: boolean;
  aiPhoto?: boolean;
  // Sold only for AI video templates (the classic video packages are made by the team).
  aiVideo?: boolean;
  caption?: boolean;
  seconds?: number;
  videoType?: ProductId;
  issuesVoucher?: { product: ProductId; uses: number; days: number };
}

export const PRODUCTS: Record<ProductId, Product> = {
  image_one: { id: 'image_one', kind: 'image', price: 3000, allFormats: false, credit: true, aiPhoto: false, caption: false },
  image_pro: { id: 'image_pro', kind: 'image', price: 5000, popular: true, allFormats: true, credit: false, aiPhoto: true, caption: true },
  image_pack5: {
    id: 'image_pack5', kind: 'image', price: 20000, allFormats: true, credit: false, aiPhoto: true, caption: true,
    issuesVoucher: { product: 'image_pro', uses: 4, days: 60 },
  },
  video_photo: { id: 'video_photo', kind: 'video', price: 10000, seconds: 15 },
  video_upgrade: { id: 'video_upgrade', kind: 'video', price: 7000, seconds: 15, upgrade: true, videoType: 'video_photo' },
  video_hai: { id: 'video_hai', kind: 'video', price: 35000, seconds: 15 },
  video_ai: { id: 'video_ai', kind: 'video', price: 15000, aiVideo: true },
};
export const PRODUCT_IDS = Object.keys(PRODUCTS) as ProductId[];

// Set per package in /admin → Pricing. results = AI results made right after payment;
// iterations = extra results the customer can ask for, each with their own change to the prompt.
export interface PackagePricing { price: number; results: number; iterations: number }
export type Pricing = Record<ProductId, PackagePricing>;
export const PRICING_DEFAULTS: Pricing = {
  image_one: { price: 3000, results: 1, iterations: 1 },
  image_pro: { price: 5000, results: 1, iterations: 4 },
  image_pack5: { price: 20000, results: 1, iterations: 4 },
  video_photo: { price: 10000, results: 0, iterations: 0 },
  video_upgrade: { price: 7000, results: 0, iterations: 0 },
  video_hai: { price: 35000, results: 0, iterations: 0 },
  video_ai: { price: 15000, results: 1, iterations: 2 },
};
// Packages that use AI results; the classic videos are made by the team.
export const usesAi = (p: Product) => p.kind === 'image' || !!p.aiVideo;
export const MAX_RESULTS = 2; // at most two AI jobs run at once per order
export const MAX_ITERATIONS = 20;

export function mergePricing(saved: Partial<Record<string, Partial<PackagePricing>>> | null | undefined): Pricing {
  return Object.fromEntries(PRODUCT_IDS.map((id) => [id, { ...PRICING_DEFAULTS[id], ...(saved?.[id] ?? {}) }])) as Pricing;
}
// Prices are read from PRODUCTS everywhere, so the admin's prices are written into it.
export function applyPricing(p: Pricing) {
  for (const id of PRODUCT_IDS) PRODUCTS[id].price = p[id].price;
}
export const aiTokens = (p: Pricing) => Object.fromEntries(PRODUCT_IDS.map((id) => [id, usesAi(PRODUCTS[id]) ? p[id].results + p[id].iterations : 0])) as Record<ProductId, number>;

export const productsFor = (kind: Kind): Product[] => Object.values(PRODUCTS).filter((p) => p.kind === kind && !p.upgrade && !p.aiVideo);
// Packages a template order can be bought with.
export const templateProducts = (kind: Kind): Product[] =>
  kind === 'image' ? productsFor('image') : [PRODUCTS.video_ai];
export const product = (id: unknown): Product | null => (typeof id === 'string' && id in PRODUCTS ? PRODUCTS[id as ProductId] : null);
export const DEFAULT_PRODUCT: Record<Kind, ProductId> = { image: 'image_pro', video: 'video_photo' };

export const FORMATS = {
  status: { w: 1080, h: 1920 },
  post: { w: 1080, h: 1350 },
  square: { w: 1080, h: 1080 },
} as const;
export type Format = keyof typeof FORMATS;
export const FORMAT_IDS = Object.keys(FORMATS) as Format[];

export const TEMPLATES = ['bango', 'ofa', 'kanga', 'safi', 'usiku'] as const;
export type Template = (typeof TEMPLATES)[number];

export const ACCENTS = ['#E4572E', '#F2A900', '#1B998B', '#2E86DE', '#C2185B', '#6A4C93', '#2B2D42'] as const;

export const CATEGORIES = [
  { id: 'mitindo', template: 'bango', accent: '#C2185B' },
  { id: 'chakula', template: 'ofa', accent: '#E4572E' },
  { id: 'urembo', template: 'safi', accent: '#6A4C93' },
  { id: 'simu', template: 'usiku', accent: '#2E86DE' },
  { id: 'duka', template: 'ofa', accent: '#F2A900' },
  { id: 'huduma', template: 'kanga', accent: '#1B998B' },
  { id: 'nyumba', template: 'safi', accent: '#2B2D42' },
  { id: 'nyingine', template: 'bango', accent: '#E4572E' },
] as const satisfies readonly { id: string; template: Template; accent: string }[];
export type CategoryId = (typeof CATEGORIES)[number]['id'];
export const CATEGORY_IDS = CATEGORIES.map((c) => c.id) as CategoryId[];

export const MAX_PHOTOS = 5;

export const VOUCHER_PRODUCTS: ProductId[] = ['image_one', 'image_pro'];
export const AGENT_PRICES: Partial<Record<ProductId, number>> = { image_one: 2000, image_pro: 3500 };
export const AGENT_MIN_BATCH = 20;

export const STATUSES = ['draft', 'pending_payment', 'paid', 'ready', 'in_production', 'cancelled'] as const;
export type Status = (typeof STATUSES)[number];
export const PAID_STATUSES: Status[] = ['paid', 'ready', 'in_production'];
export const isPaidStatus = (s: string) => (PAID_STATUSES as string[]).includes(s);

export const IMAGE_STYLES = ['studio', 'lifestyle', 'street', 'luxury', 'bright'] as const;
export type ImageStyle = (typeof IMAGE_STYLES)[number];

export const VIDEO_TYPES: ProductId[] = ['video_photo', 'video_hai'];

// AI image quality the owner picks on the create page. High is a 2K image and costs two tokens.
export const QUALITIES = ['standard', 'high'] as const;
export type Quality = (typeof QUALITIES)[number];
export const QUALITY_TOKENS: Record<Quality, number> = { standard: 1, high: 2 };
export const tokensPerResult = (q: unknown) => QUALITY_TOKENS[q as Quality] ?? 1;

// What the owner tells us on the create page beyond the basic details:
// packages (several things on offer, each with its own words and price) and
// answers to questions the AI asks after looking at the photos.
export interface BriefPackage { name: string; desc: string; price: string }
export interface BriefQuestion { key: string; q: string; ph: string }
// context: notes the owner names themselves, e.g. { label: 'Colours to use', value: 'red and white' }.
export interface BriefContext { label: string; value: string }
// What the create page is making: a business ad, or something for fun or for the person themselves (spec: not only businesses).
export const PURPOSES = ['business', 'meme', 'personal', 'other'] as const;
export type Purpose = (typeof PURPOSES)[number];
// The mood a non-business creation should have.
export const TONES = ['funny', 'beautiful', 'dramatic', 'natural'] as const;
export type Tone = (typeof TONES)[number];
export interface Brief { packages: BriefPackage[]; questions: BriefQuestion[]; answers: Record<string, string>; types: string[]; context: BriefContext[]; purpose?: Purpose; tone?: Tone | null }
export const MAX_PACKAGES = 6;
export const EMPTY_BRIEF: Brief = { packages: [], questions: [], answers: {}, types: [], context: [], purpose: 'business', tone: null };
// Orders made before purposes existed are business ads.
export const isBusiness = (b: Pick<Brief, 'purpose'> | null | undefined) => !b?.purpose || b.purpose === 'business';
export const MAX_CONTEXT = 8;
export const MAX_TYPES = 4;

// Kept on the account; a new ad starts with it. logo is a file name in the user's folder.
// The brand kit (spec §8): location and colours are used by v2 templates' brand_* fields.
export interface BusinessProfile { name: string; types: string[]; logo: string | null; phone: string; location: string; colors: string[] }
export const EMPTY_PROFILE: BusinessProfile = { name: '', types: [], logo: null, phone: '', location: '', colors: [] };

// ---------- templates ----------

export const ASPECTS = ['9:16', '4:5', '1:1', '16:9'] as const;
export type Aspect = (typeof ASPECTS)[number];
export const ASPECT_RATIO: Record<Aspect, number> = { '9:16': 9 / 16, '4:5': 4 / 5, '1:1': 1, '16:9': 16 / 9 };
export const TEMPLATE_STATUSES = ['draft', 'pending', 'changes', 'approved', 'rejected', 'archived'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];
// Business fields a template can ask for; designers can add their own fields too.
export const CORE_FIELDS = ['business', 'offer', 'price', 'phone', 'location'] as const;
export type CoreField = (typeof CORE_FIELDS)[number];
export const PLACEHOLDER = /\{([a-z][a-z0-9_]{0,23})\}/g;

export interface TemplateField {
  key: string;
  label: { sw: string; en: string };
  type: 'text' | 'textarea';
  required: boolean;
  max: number;
}
export interface TemplateInputs {
  photos: { min: number; max: number; hint: { sw: string; en: string } };
  core: CoreField[];
  fields: TemplateField[];
}
export const TEXT_KEYS = ['headline', 'subline', 'offer', 'cta'] as const;
export const EVENT_TYPES = ['visit', 'preview', 'download', 'share'] as const;

// Sign-in code while no SMS provider is switched on (SMS mode "mock"). Real SMS codes are 6 random digits.
export const MOCK_CODE = '1234';

export const SITE_CREDIT = 'ads.m-changisha.online';
